import i18n from "../../i18n";
import React from "react";
import { Button } from "@/components/ui/button";
import { ChevronRight, Pencil, Trash2 } from "lucide-react";

/**
 * Non-recursive tree flattening — walk the position tree and produce a flat
 * list of nodes with `depth`. We render iteratively with left-padding to avoid
 * self-referencing JSX (which trips the visual-edits babel plugin).
 * Guards against parent-id cycles via a `seen` set.
 */
function flattenTree(parentId, groups, depth, out, seen) {
    const list = groups[parentId] || [];
    for (const p of list) {
        if (seen.has(p.id)) continue;
        seen.add(p.id);
        out.push({ node: p, depth });
        flattenTree(p.id, groups, depth + 1, out, seen);
    }
    return out;
}

export default function OrgTree({ groups, employees, onEdit, onDelete }) {
    const seen = new Set();
    const flat = flattenTree("__root__", groups, 0, [], seen);
    // Collect orphans — positions whose parent_id points to a deleted/missing node.
    const orphans = [];
    Object.keys(groups).forEach((k) => {
        if (k === "__root__") return;
        if (!seen.has(k) && k !== "__orphans__") {
            (groups[k] || []).forEach((p) => {
                if (!seen.has(p.id)) {
                    seen.add(p.id);
                    orphans.push({ node: p, depth: 0 });
                }
            });
        }
    });
    const all = [...flat, ...orphans];
    if (all.length === 0) return null;
    return (
        <div>
            {orphans.length > 0 && (
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-3 mb-1">
                    {i18n.t("batch1.01df8e3c852a")}
                </div>
            )}
            {all.map(({ node: p, depth }) => (
                <div
                    key={p.id}
                    data-testid={`org-node-${p.id}`}
                    style={{ marginLeft: depth * 24 }}
                    className={depth > 0 ? "border-l border-border pl-3" : ""}
                >
                    <div className="flex items-center justify-between border rounded-lg px-3 py-2 mb-1 bg-card hover:bg-muted/30">
                        <div className="flex items-center gap-2 min-w-0">
                            <ChevronRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                            <div className="min-w-0">
                                <div className="font-semibold text-sm truncate">{p.title}</div>
                                <div className="text-xs text-muted-foreground truncate">
                                    {p.department || "—"}
                                    {p.employee_id && employees[p.employee_id]
                                        ? ` · ${employees[p.employee_id].name}`
                                        : p.employee_name
                                            ? ` · ${p.employee_name}`
                                            : ""}
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                            <Button size="sm" variant="ghost" onClick={() => onEdit(p)} data-testid={`edit-position-${p.id}`}>
                                <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-destructive" onClick={() => onDelete(p)}>
                                <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}
